// Modules
import {PropsWithChildren} from 'react';
import {Navigate} from 'react-router-dom';

// Utils
import {IUser, UserDiscriminator} from 'features/auth/authTypes';
import {MiddlewareComponent} from 'routes/types';
import {store} from 'app/store';
import {selectAuthUser} from 'features/auth/authSlice';
import {Permission} from 'features/roles';

/**
 * Check if a regular user has a specific permission.
 * SuperAdmins have all permissions.
 * Other users must have the permission in their permissions array.
 */
const checkUserPermission = (user: IUser, permission: Permission): boolean => {
  // SuperAdmin has all permissions
  if (user.superAdmin === true) {
    return true;
  }

  // Check if user has the permission in their permissions array
  const userPermissions = user.permissions ?? [];
  return userPermissions.includes(permission);
};

/**
 * Check if a regular user has ALL of the specified permissions.
 * SuperAdmins have all permissions.
 * Returns the list of missing permissions.
 */
const checkUserPermissions = (
  user: IUser,
  permissions: Permission[],
): {hasAll: boolean; missing: Permission[]} => {
  // SuperAdmin has all permissions
  if (user.superAdmin === true) {
    return {hasAll: true, missing: []};
  }

  const userPermissions = user.permissions ?? [];
  const missing = permissions.filter((p) => !userPermissions.includes(p));

  return {
    hasAll: missing.length === 0,
    missing,
  };
};

/**
 * Check if a regular user is an admin (superAdmin flag or has admin permissions)
 */
const isUserAdmin = (user: IUser): boolean => {
  if (user.superAdmin === true) return true;
  const userPermissions = user.permissions ?? [];
  return userPermissions.includes(Permission.MANAGE_USERS) && userPermissions.includes(Permission.MANAGE_PERMISSIONS);
};

/**
 * Middleware que solo permite acceso a usuarios con rol CUSTOMER
 * Redirige a /customers/login si no es customer
 */
export const onlyCustomerAllowedMiddleware: MiddlewareComponent = ({
  children,
}) => {
  const authUser = selectAuthUser(store.getState());

  if (!authUser || authUser.discriminator !== UserDiscriminator.CUSTOMER) {
    return <Navigate to="/customers/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Middleware que protege rutas de usuarios regulares (no customers, no partners)
 * Redirige customers a /customers, partners a /partners, y usuarios no autenticados a /login
 */
export const exceptCustomerProtectMiddleware: MiddlewareComponent = ({
  children,
}) => {
  const authUser = selectAuthUser(store.getState());

  if (!authUser) {
    return <Navigate to="/login" replace />;
  }

  if (authUser.discriminator === UserDiscriminator.CUSTOMER) {
    return <Navigate to="/customers" replace />;
  }

  if (authUser.discriminator === UserDiscriminator.PARTNER) {
    return <Navigate to="/partners" replace />;
  }

  return <>{children}</>;
};

/**
 * Factory que crea un middleware para redirigir customers a una ruta específica
 * @param redirectTo - Ruta a la que redirigir si el usuario es customer
 * @returns MiddlewareComponent
 */
export const createCustomerRedirectMiddleware = (
  redirectTo: string,
): MiddlewareComponent => {
  return ({children}: PropsWithChildren) => {
    const authUser = selectAuthUser(store.getState());

    if (authUser?.discriminator === UserDiscriminator.CUSTOMER) {
      return <Navigate to={redirectTo} replace />;
    }

    return <>{children}</>;
  };
};

/**
 * Factory que crea un middleware para verificar un permiso específico.
 * Redirige a /forbidden si el usuario no tiene el permiso requerido.
 * @param requiredPermission - Permiso requerido para acceder a la ruta
 * @returns MiddlewareComponent
 */
export const createPermissionMiddleware = (
  requiredPermission: Permission,
): MiddlewareComponent => {
  return ({children}: PropsWithChildren) => {
    const authUser = selectAuthUser(store.getState());

    // User must be authenticated and a regular user (not customer)
    if (!authUser || authUser.discriminator !== UserDiscriminator.REGULAR) {
      return <Navigate to="/login" replace />;
    }

    // Check if user has the required permission
    const hasPermission = checkUserPermission(authUser as IUser, requiredPermission);

    if (!hasPermission) {
      return (
        <Navigate
          to="/forbidden"
          state={{missingPermissions: [requiredPermission]}}
          replace
        />
      );
    }

    return <>{children}</>;
  };
};

/**
 * Factory que crea un middleware para verificar múltiples permisos.
 * El usuario debe tener TODOS los permisos especificados.
 * Redirige a /forbidden mostrando los permisos faltantes.
 * @param requiredPermissions - Array de permisos requeridos para acceder a la ruta
 * @returns MiddlewareComponent
 */
export const createMultiplePermissionsMiddleware = (
  requiredPermissions: Permission[],
): MiddlewareComponent => {
  return ({children}: PropsWithChildren) => {
    const authUser = selectAuthUser(store.getState());

    // User must be authenticated and a regular user (not customer)
    if (!authUser || authUser.discriminator !== UserDiscriminator.REGULAR) {
      return <Navigate to="/login" replace />;
    }

    // Check if user has all required permissions
    const {hasAll, missing} = checkUserPermissions(
      authUser as IUser,
      requiredPermissions,
    );

    if (!hasAll) {
      return (
        <Navigate
          to="/forbidden"
          state={{missingPermissions: missing}}
          replace
        />
      );
    }

    return <>{children}</>;
  };
};

/**
 * Middleware que solo permite acceso a usuarios con rol ADMIN o superAdmin.
 * Redirige a /forbidden si el usuario no es admin.
 */
export const adminOnlyMiddleware: MiddlewareComponent = ({children}) => {
  const authUser = selectAuthUser(store.getState());

  // User must be authenticated and a regular user (not customer)
  if (!authUser || authUser.discriminator !== UserDiscriminator.REGULAR) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin
  if (!isUserAdmin(authUser as IUser)) {
    return (
      <Navigate
        to="/forbidden"
        state={{missingPermissions: []}}
        replace
      />
    );
  }

  return <>{children}</>;
};

/**
 * Middleware that only allows access to users with PARTNER discriminator.
 * Redirects to /partners/login if not a partner.
 */
export const onlyPartnerAllowedMiddleware: MiddlewareComponent = ({
  children,
}) => {
  const authUser = selectAuthUser(store.getState());

  if (!authUser || authUser.discriminator !== UserDiscriminator.PARTNER) {
    return <Navigate to="/partners/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Factory that creates a middleware to redirect partners to a specific route.
 * @param redirectTo - Route to redirect to if the user is a partner
 * @returns MiddlewareComponent
 */
export const createPartnerRedirectMiddleware = (
  redirectTo: string,
): MiddlewareComponent => {
  return ({children}: PropsWithChildren) => {
    const authUser = selectAuthUser(store.getState());

    if (authUser?.discriminator === UserDiscriminator.PARTNER) {
      return <Navigate to={redirectTo} replace />;
    }

    return <>{children}</>;
  };
};
